import { Dtos } from "@inrev/common";
import { Option } from "@inrev/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { accountQueryKey } from "../../account/account.keys";
import { useCreateAccountIndemnityAgreement } from "../../account/agent";
import { bondPreviewsQueryKey } from "../../bond/bond.keys";
import { bondRequestPreviewsQueryKey } from "../bond-request.keys";
import { invalidateBondRequestAfterIssue, redirectToIssuedBond } from "../utils";
import { BondRequest } from "./types";

export const useIssueBond = (
	request: BondRequest,
	indemnityAgreement: Option<Dtos.SuretyAccount.IndemnityAgreement.Get.Response>,
) => {
	const queryClient = useQueryClient();
	const { createAccountIndemnityAgreement } = useCreateAccountIndemnityAgreement();
	const { post } = useRequest();

	const mutation = useMutation({
		meta: { redirectOnError: redirectToIssuedBond },
		mutationFn: async (data: {
			bondForm: Dtos.SuretyBondIssuance.Create.Request;
			indemnityAgreement?: Dtos.IndemnityAgreement.Create.Request;
		}): Promise<void> => {
			const bondFormData = data.bondForm;
			const indemnityAgreementData = (() => {
				if (!indemnityAgreement) {
					if (data.indemnityAgreement === undefined) {
						throw new Error("Indemnity agreement is required to issue bond");
					}
					return {
						accountId: request.suretyAccountId,
						data: data.indemnityAgreement,
					};
				}
			})();
			if (indemnityAgreementData !== undefined) {
				await createAccountIndemnityAgreement({
					accountId: indemnityAgreementData.accountId,
					data: indemnityAgreementData.data,
				});
			}
			await post(
				`/v2/surety/accounts/${request.suretyAccountId}/quotes/${request.id}/issue`,
				bondFormData,
			);
		},
		onSuccess: () => {
			queryClient.refetchQueries({ queryKey: bondRequestPreviewsQueryKey() });
			queryClient.invalidateQueries({ queryKey: bondPreviewsQueryKey() });
			queryClient.invalidateQueries({ queryKey: accountQueryKey(request.suretyAccountId) });
		},
		onSettled: () => {
			void invalidateBondRequestAfterIssue(queryClient, request.id);
		},
	});

	return { issueBond: mutation.mutate, issueBondIsLoading: mutation.isPending };
};
